import { useCreatePermissionGroup } from "@entities/policies/lib/hooks/useCreatePermissionGroup";
import { useEditPermissionGroup } from "@entities/policies/lib/hooks/useEditPermissionGroup";
import { useGetBrandPermissions } from "@entities/policies/lib/hooks/useGetBrandPermissions";
import { PermissionGroup } from "@entities/policies/ui";
import { ToastService } from "@shared/lib/services";
import { PermissionGroup as IPermissionGroup } from "@shared/lib/types/permissions";
import { Button, Card, Form, Input, Typography } from "@shared/ui";
import { Divider, Flex, Space, Spin } from "antd";
import { Save } from "lucide-react";
import { useEffect, useMemo } from "react";
import styles from "./styles.module.scss";

const { Title } = Typography;

export const PolicyForm = ({
  isEdit,
  policy,
  onCancel,
  brandOrganisationId,
  queryKey,
}: {
  isEdit: boolean;
  policy?: IPermissionGroup;
  onCancel?: () => void;
  brandOrganisationId: string;
  queryKey?: any;
}) => {
  const [form] = Form.useForm();
  const { brandPermissions, isFetchingBrandPermissions } =
    useGetBrandPermissions();
  const {
    createPermissionGroup,
    isPermissionGroupCreating,
    isPermissionGroupCreated,
    permissionGroupError,
  } = useCreatePermissionGroup(queryKey);
  const {
    editPermissionGroup,
    isPermissionGroupEditing,
    isPermissionGroupEdited,
    permissionGroupEditError,
  } = useEditPermissionGroup(queryKey);

  useEffect(() => {
    if (isPermissionGroupCreated) {
      ToastService.success("Policy created successfully");
      onCancel?.();
    } else if (permissionGroupError) {
      ToastService.error(permissionGroupError?.message);
    }
  }, [isPermissionGroupCreated, permissionGroupError]);

  useEffect(() => {
    if (isPermissionGroupEdited) {
      ToastService.success("Policy edited successfully");
      onCancel?.();
    } else if (permissionGroupEditError) {
      ToastService.error(permissionGroupEditError?.message);
    }
  }, [isPermissionGroupEdited, permissionGroupEditError]);

  const handleSave = (values: any) => {
    if (!isEdit) {
      // create new permission group
      createPermissionGroup({
        brandOrganisationId: brandOrganisationId || "",
        name: values.name,
        permissions: values.permissions || [],
        description: values.description,
      });
    } else if (isEdit) {
      editPermissionGroup({
        brandOrganisationId: brandOrganisationId || "",
        name: values.name,
        permissions: values.permissions || [],
        description: values.decription,
        permissionGroupId: policy?._id || "",
      });
    }
  };

  const allPermissions = useMemo(() => {
    return Object.values(brandPermissions?.data || {}).flat();
  }, [brandPermissions]);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSave}
      initialValues={{ ...policy }}
      className={styles.form}
      requiredMark={false}
    >
      <Space
        direction="vertical"
        size="middle"
        style={{ width: "100%", marginBottom: 70 }}
      >
        <Card
          styles={{
            body: {
              padding: "17.5px",
            },
          }}
        >
          <Form.Item
            name="name"
            label={
              <span>
                Policy Name <span className={styles.asterisk}>*</span>
              </span>
            }
            rules={[{ required: true }]}
            style={{ marginBottom: 10 }}
          >
            <Input
              placeholder="e.g. Campaign Manager"
              style={{ backgroundColor: "#fcfdfe" }}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea
              rows={2}
              placeholder="What is this role for?"
              style={{ backgroundColor: "#fcfdfe" }}
            />
          </Form.Item>
        </Card>

        <Title level={5}>Permissions</Title>

        <Form.Item name="permissions" noStyle />
        <Spin spinning={isFetchingBrandPermissions}>
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {brandPermissions &&
              Object.keys(brandPermissions?.data || {}).map((groupName) => (
                <PermissionGroup
                  key={groupName}
                  groupName={groupName}
                  allPermissions={allPermissions}
                  // group={brandPermissions?.data[groupName]}
                />
              ))}
          </Space>
        </Spin>
      </Space>

      <div
        style={{
          width: "100%",
          position: "absolute",
          bottom: 0,
          left: 0,
          backgroundColor: "#fcfdfe",
          zIndex: 100,
        }}
      >
        <Divider style={{ margin: "0 0 20px 0" }} />

        <Flex
          gap={10}
          justify="flex-end"
          style={{ marginRight: 20, marginBottom: 20 }}
        >
          <Button
            onClick={onCancel}
            disabled={isPermissionGroupCreating || isPermissionGroupEditing}
            type="text"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            icon={<Save size={16} />}
            loading={isPermissionGroupCreating || isPermissionGroupEditing}
          >
            Save Policy
          </Button>
        </Flex>
      </div>
    </Form>
  );
};
